import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useId, useState, useTransition } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { signUpUser } from "$/modules/auth/lib/auth";
import { AuthLayout } from "$/modules/auth/ui/auth-layout";
import { OAuthButtons } from "$/modules/auth/ui/oauth-buttons";
import { PasswordInput } from "$/modules/auth/ui/password-input";
import { Button } from "$/modules/shared/ui/button";
import { Field, FieldLabel } from "$/modules/shared/ui/field";
import { Input } from "$/modules/shared/ui/input";
import { Spinner } from "$/modules/shared/ui/spinner";

export const Route = createFileRoute("/$locale/signup/")({
	component: SignUpPage,
});

function SignUpPage() {
	const fieldId = useId();

	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const t = useTranslations();
	const { locale } = Route.useParams();
	const redirect =
		new URLSearchParams(window.location.search).get("redirect") ?? undefined;
	const [isPending, startTransition] = useTransition();

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		if (password !== confirm) {
			toast.error(t("auth.passwordMismatch"), {});
			return;
		}

		startTransition(async () => {
			const verificationUrl = `${window.location.origin}/${locale}/verify-email`;
			const response = await signUpUser(
				{ name, email, password },
				verificationUrl,
			);
			if (!response.success) {
				toast.error(response.message, {});
				return;
			}

			toast.success(response.message, {});
			window.location.assign(
				`/${locale}/signin${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`,
			);
		});
	};

	return (
		<AuthLayout
			locale={locale}
			title={t("signUp")}
			subtitle={t("auth.signUpDescription")}
			footer={
				<span>
					{t("auth.hasAccount")}{" "}
					<a
						href={`/${locale}/signin${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`}
						className="font-semibold text-primary hover:underline"
					>
						{t("signIn")}
					</a>
				</span>
			}
		>
			<form onSubmit={handleSubmit} className="space-y-4">
				<Field>
					<FieldLabel htmlFor={`${fieldId}-100`}>{t("auth.name")}</FieldLabel>
					<Input
						id={`${fieldId}-100`}
						autoComplete="name"
						minLength={2}
						placeholder={t("auth.namePlaceholder")}
						value={name}
						required
						onChange={(event) => setName(event.currentTarget.value)}
					/>
				</Field>
				<Field>
					<FieldLabel htmlFor={`${fieldId}-101`}>{t("email")}</FieldLabel>
					<Input
						id={`${fieldId}-101`}
						autoComplete="email"
						type="email"
						placeholder={t("emailPlaceholder")}
						value={email}
						required
						onChange={(event) => setEmail(event.currentTarget.value)}
					/>
				</Field>
				<PasswordInput
					label={t("password")}
					placeholder={t("passwordPlaceholder")}
					value={password}
					onValueChange={setPassword}
					autoComplete="new-password"
					isRequired
					minLength={8}
				/>
				<PasswordInput
					label={t("auth.confirmPassword")}
					placeholder={t("passwordPlaceholder")}
					value={confirm}
					onValueChange={setConfirm}
					autoComplete="new-password"
					isRequired
					minLength={8}
				/>

				<Button
					type="submit"
					size="lg"
					className="w-full font-semibold"
					disabled={isPending}
					variant="default"
				>
					{isPending && <Spinner data-icon="inline-start" />}
					{t("createAccount")}
				</Button>
			</form>

			<OAuthButtons locale={locale} returnTo={redirect} />
		</AuthLayout>
	);
}
